import React, { useState } from 'react';

import { graphql, useFragment } from 'react-relay';

import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';

import { type ProductKeys } from '../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import type { SearchResultSharedProps } from '../../../../common/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { isLeapQueryEnabled } from '../../../../common/utils/is-leap-query-enabled';
import { ThirdPartyQuerySuggestionsContainer } from '../../../../common/utils/third-party-query-suggestions-container';
import { useAnalytics } from '../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useIsUserOAuthed } from '../../../../controllers/oauth';
import { useSearchActions } from '../../../../controllers/store/search';

import type { searchResult_AtlassianSearchPage$key } from './__generated__/searchResult_AtlassianSearchPage.graphql';
import { BulkConnectBanner } from './bulk-connect-banner';
import { LeapQueryBanner } from './leap-query-banner';
import { SearchResultRenderer } from './search-result-renderer';
import type { JiraIssueStatusAndColorName } from './search-result-renderer/search-jira-issue';

// How many search results to show before the OAuth banner
export const OAUTH_BANNER_POSITION = 4;
const LEAP_QUERY_BANNER_POSITION = 5;
const THIRD_PARTY_QUERY_SUGGESTION_POSITION = 5;

export type SearchResultProps = SearchResultSharedProps & {
	node: searchResult_AtlassianSearchPage$key;
	position: number;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	originalQuery: string | null | undefined;
	logError: (error: any) => Promise<void>;
	compassTeamName?: string | null; // Optional, used for Compass teams
	jiraIssueStatus?: JiraIssueStatusAndColorName | null; // Optional, used for Jira issues
	jiraLinkedPRs?: IssueLinkedPRsEntry; // Optional, used for Jira issue linked PRs
	is3pGroupingEnabled?: boolean;
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	mainContentWidth?: number;
	/**
	 * When true, the parent list renderer has opted into the lite
	 * layout for this result. Leaf renderers that support a lite variant will
	 * render their lite layout instead of the standard SearchResult card.
	 *
	 * This is the single source of truth for lite-mode rendering — leaf
	 * renderers MUST NOT independently consult feature flags to decide between
	 * lite and default rendering. The lite list renderer (SearchResultsListLiteRenderer)
	 * sets this to true only when its own preconditions are met (e.g. an eligible
	 * product filter is selected and there are results to show). This prevents the
	 * lite row from leaking into code paths that should always render the standard card
	 * layout when the lite-mode feature is enabled at the bootstrap level.
	 */
	isLiteMode?: boolean;
};

export const SearchResult = ({
	node,
	position,
	thirdPartyConfigs,
	availableProducts,
	cloudId,
	searchQuery,
	originalQuery,
	selectedProduct,
	searchSessionId,
	primaryProduct,
	appMode,
	logError,
	compassTeamName,
	jiraIssueStatus,
	jiraLinkedPRs,
	is3pGroupingEnabled,
	smartAnswersPortalRef,
	mainContentWidth,
	isLiteMode,
}: SearchResultProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchResult_AtlassianSearchPage on SearchResult
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeAssets: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
			) {
				id
				...searchResultRenderer_AtlassianSearchPage
					@arguments(
						includeAssigneeField: $includeAssigneeField
						includeJiraLiteModeFields: $includeJiraLiteModeFields
						includeIconUrl: $includeIconUrl
						includeAssets: $includeAssets
						includePassionfruitAsk: $includePassionfruitAsk
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
					)
			}
		`,
		node,
	);

	const { isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
	const [areThirdPartyQuerySuggestionsVisible, setAreThirdPartyQuerySuggestionsVisible] =
		useState(false);

	return (
		<>
			{position === OAUTH_BANNER_POSITION && (
				<BulkConnectBanner
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					availableProducts={availableProducts}
				/>
			)}
			{position === LEAP_QUERY_BANNER_POSITION &&
				isLeapQueryEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled) &&
				!areThirdPartyQuerySuggestionsVisible && (
					<LeapQueryBanner
						searchQuery={searchQuery}
						originalQuery={originalQuery}
						primaryProduct={primaryProduct}
						logError={logError}
					/>
				)}
			{position === THIRD_PARTY_QUERY_SUGGESTION_POSITION && !selectedProduct && (
				<ThirdPartyQuerySuggestionsContainer
					thirdPartyConfigs={thirdPartyConfigs}
					useIsUserOAuthed={useIsUserOAuthed}
					useSearchActions={useSearchActions}
					useAnalytics={useAnalytics}
					currentQuery={searchQuery}
					onVisible={() => setAreThirdPartyQuerySuggestionsVisible(true)}
				/>
			)}
			<ErrorBoundary section="SearchPageResultRecord">
				<SearchResultRenderer
					key={data.id}
					node={data}
					position={position}
					cloudId={cloudId}
					searchQuery={searchQuery}
					searchSessionId={searchSessionId}
					appMode={appMode}
					primaryProduct={primaryProduct}
					selectedProduct={selectedProduct}
					thirdPartyConfigs={thirdPartyConfigs}
					compassTeamName={compassTeamName}
					jiraIssueStatus={jiraIssueStatus}
					jiraLinkedPRs={jiraLinkedPRs}
					is3pGroupingEnabled={is3pGroupingEnabled}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
					isLiteMode={isLiteMode}
				/>
			</ErrorBoundary>
		</>
	);
};
